import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import socialQueueApi from '../services/socialQueueApi';
import ConnectedAccounts from './ConnectedAccounts';
import { 
  Send, Clock, CheckCircle, XCircle, Trash2, Edit, ImagePlus, 
  Upload, Sparkles, Calendar, RefreshCw, Link2, ExternalLink,
  Twitter, Linkedin, Facebook, Instagram, Youtube, MessageCircle,
  Loader2, AlertCircle, Copy, X, ChevronDown, Eye, Settings, User as UserIcon
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Platform icons mapping
const PlatformIcon = ({ platform, className = "w-4 h-4" }) => {
  const iconMap = {
    twitter: Twitter,
    linkedin: Linkedin,
    facebook: Facebook,
    instagram: Instagram,
    youtube: Youtube,
    bluesky: MessageCircle,
    threads: MessageCircle,
    tiktok: MessageCircle,
    reddit: MessageCircle,
    mastodon: MessageCircle,
    pinterest: MessageCircle,
  };
  const Icon = iconMap[platform?.toLowerCase()] || MessageCircle;
  return <Icon className={className} />;
};

// Platform badge colors
const getPlatformColor = (platform) => {
  const colors = {
    twitter: 'bg-sky-500/20 text-sky-400 border-sky-500/30',
    linkedin: 'bg-blue-600/20 text-blue-400 border-blue-500/30',
    facebook: 'bg-blue-500/20 text-blue-300 border-blue-400/30',
    instagram: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
    youtube: 'bg-red-500/20 text-red-400 border-red-500/30',
    bluesky: 'bg-sky-400/20 text-sky-300 border-sky-400/30',
    threads: 'bg-gray-500/20 text-gray-300 border-gray-500/30',
    tiktok: 'bg-black text-white border-gray-700',
    reddit: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    mastodon: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    pinterest: 'bg-red-600/20 text-red-400 border-red-500/30',
  };
  return colors[platform?.toLowerCase()] || 'bg-brand-gray-700 text-brand-gray-300 border-brand-gray-600';
};

// Social Post Card Component
const SocialPostCard = ({ post, onUpdate, onDelete, onPublish, onGenerateImage }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(post.text);
  const [showScheduler, setShowScheduler] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);
  const [showConfirmRegenerate, setShowConfirmRegenerate] = useState(false);

  const handleSaveEdit = async () => {
    await onUpdate(post.id, { text: editText });
    setIsEditing(false);
  };

  const handlePublish = async (scheduled = false) => {
    setIsPublishing(true);
    try {
      const scheduleFor = scheduled && scheduleDate && scheduleTime 
        ? new Date(`${scheduleDate}T${scheduleTime}`).toISOString() 
        : null;
      await onPublish(post.id, scheduleFor);
      setShowScheduler(false);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleRegenerateImage = () => {
    if (post.image_user_uploaded) {
      setShowConfirmRegenerate(true);
    } else {
      onGenerateImage(post.id);
    }
  };

  const confirmRegenerate = () => {
    setShowConfirmRegenerate(false);
    onGenerateImage(post.id);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(post.text);
    toast.success('Caption copied!');
  };

  return (
    <div className="bg-brand-gray-900 border border-brand-gray-700 rounded-xl overflow-hidden hover:border-brand-orange/30 transition-colors" data-testid={`social-post-${post.id}`}>
      {/* Header */}
      <div className="p-4 border-b border-brand-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          {post.platforms.map(platform => (
            <span 
              key={platform} 
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${getPlatformColor(platform)}`}
            >
              <PlatformIcon platform={platform} className="w-3 h-3" />
              {platform}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {/* Status badge */}
          <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${
            post.status === 'published' ? 'bg-green-900/30 text-green-400' :
            post.status === 'queued' ? 'bg-brand-orange/20 text-brand-orange' :
            post.status === 'failed' ? 'bg-red-900/30 text-red-400' :
            'bg-brand-gray-700 text-brand-gray-400'
          }`}>
            {post.status}
          </span>
        </div>
      </div>

      {/* Image */}
      {post.image?.url && (
        <div className="relative">
          <img 
            src={`${BACKEND_URL}${post.image.url}`} 
            alt="" 
            className="w-full aspect-video object-cover"
            data-testid={`post-image-${post.id}`}
          />
          {/* Image source badge */}
          <div className="absolute top-2 left-2">
            {post.image_source === 'user_upload' ? (
              <span className="px-2 py-1 bg-emerald-600/90 text-white text-[10px] font-bold rounded-full flex items-center gap-1">
                <Upload className="w-3 h-3" /> Your upload
              </span>
            ) : (
              <span className="px-2 py-1 bg-brand-gray-800/90 text-brand-gray-300 text-[10px] rounded-full">
                via {post.image?.model_used || 'AI'}
              </span>
            )}
          </div>
          {/* Regenerate button */}
          <button
            onClick={handleRegenerateImage}
            className="absolute top-2 right-2 p-2 bg-brand-gray-800/90 hover:bg-brand-orange text-white rounded-full transition-colors"
            title="Regenerate image"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* No image placeholder */}
      {!post.image?.url && post.image_status !== 'generating' && (
        <div className="p-6 bg-brand-gray-800/50 flex flex-col items-center justify-center gap-3">
          <ImagePlus className="w-8 h-8 text-brand-gray-500" />
          <button
            onClick={() => onGenerateImage(post.id)}
            className="text-sm text-brand-orange hover:underline flex items-center gap-1"
          >
            <Sparkles className="w-4 h-4" /> Generate AI Image
          </button>
        </div>
      )}

      {/* Generating image state */}
      {post.image_status === 'generating' && (
        <div className="p-6 bg-brand-gray-800/50 flex flex-col items-center justify-center gap-3">
          <Loader2 className="w-8 h-8 text-brand-orange animate-spin" />
          <span className="text-sm text-brand-gray-400">Generating image...</span>
        </div>
      )}

      {/* Content */}
      <div className="p-4 space-y-3">
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              className="w-full bg-black border border-brand-gray-600 rounded-lg p-3 text-white text-sm resize-none focus:border-brand-orange outline-none"
              rows={4}
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={handleSaveEdit}
                className="px-3 py-1.5 bg-brand-orange text-black text-xs font-bold rounded-lg"
              >
                Save
              </button>
              <button
                onClick={() => { setIsEditing(false); setEditText(post.text); }}
                className="px-3 py-1.5 bg-brand-gray-700 text-white text-xs rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-brand-gray-200 whitespace-pre-wrap leading-relaxed">
            {post.text}
          </p>
        )}

        {/* Hashtags */}
        {post.hashtags && post.hashtags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {post.hashtags.map((tag, idx) => (
              <span key={idx} className="text-xs text-brand-orange">#{tag}</span>
            ))}
          </div>
        )}

        {/* Scheduled time */}
        {post.scheduled_for && (
          <div className="flex items-center gap-2 text-xs text-brand-gray-400">
            <Clock className="w-3 h-3" />
            Scheduled: {new Date(post.scheduled_for).toLocaleString()}
          </div>
        )}

        {/* Publish results */}
        {post.publish_results && Object.keys(post.publish_results).length > 0 && (
          <div className="space-y-1">
            {Object.entries(post.publish_results).map(([platform, result]) => (
              <div key={platform} className="flex items-center gap-2 text-xs">
                <PlatformIcon platform={platform} className="w-3 h-3" />
                {result.ok ? (
                  <>
                    <CheckCircle className="w-3 h-3 text-green-400" />
                    <a href={result.url} target="_blank" rel="noopener noreferrer" className="text-brand-orange hover:underline">
                      View post <ExternalLink className="w-3 h-3 inline" />
                    </a>
                  </>
                ) : (
                  <>
                    <XCircle className="w-3 h-3 text-red-400" />
                    <span className="text-red-400">{result.error}</span>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      {post.status === 'draft' && (
        <div className="p-4 border-t border-brand-gray-700 flex flex-wrap gap-2">
          <button
            onClick={() => setIsEditing(true)}
            className="flex-1 px-3 py-2 text-xs font-medium text-brand-gray-300 bg-brand-gray-700/50 hover:bg-brand-gray-700 rounded-lg flex items-center justify-center gap-1"
          >
            <Edit className="w-3 h-3" /> Edit
          </button>
          <button
            onClick={copyToClipboard}
            className="px-3 py-2 text-xs font-medium text-brand-gray-300 bg-brand-gray-700/50 hover:bg-brand-gray-700 rounded-lg"
          >
            <Copy className="w-3 h-3" />
          </button>
          <button
            onClick={() => setShowScheduler(!showScheduler)}
            className="flex-1 px-3 py-2 text-xs font-medium text-brand-gray-300 bg-brand-gray-700/50 hover:bg-brand-gray-700 rounded-lg flex items-center justify-center gap-1"
          >
            <Calendar className="w-3 h-3" /> Schedule
          </button>
          <button
            onClick={() => handlePublish(false)}
            disabled={isPublishing}
            className="flex-1 px-3 py-2 text-xs font-medium text-black bg-brand-orange hover:bg-brand-orange/90 rounded-lg flex items-center justify-center gap-1 disabled:opacity-50"
          >
            {isPublishing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            Publish
          </button>
          <button
            onClick={() => onDelete(post.id)}
            className="px-3 py-2 text-xs font-medium text-red-400 hover:bg-red-900/30 rounded-lg"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Scheduler */}
      {showScheduler && post.status === 'draft' && (
        <div className="p-4 border-t border-brand-gray-700 bg-brand-gray-800/50 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-brand-gray-400 mb-1 block">Date</label>
              <input
                type="date"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                className="w-full bg-black border border-brand-gray-600 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-brand-orange"
              />
            </div>
            <div>
              <label className="text-xs text-brand-gray-400 mb-1 block">Time</label>
              <input
                type="time"
                value={scheduleTime}
                onChange={(e) => setScheduleTime(e.target.value)}
                className="w-full bg-black border border-brand-gray-600 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-brand-orange"
              />
            </div>
          </div>
          <button
            onClick={() => handlePublish(true)}
            disabled={!scheduleDate || !scheduleTime || isPublishing}
            className="w-full px-4 py-2 text-sm font-medium text-black bg-brand-orange hover:bg-brand-orange/90 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isPublishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
            Schedule Post
          </button>
        </div>
      )}

      {/* Confirm regenerate dialog */}
      {showConfirmRegenerate && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setShowConfirmRegenerate(false)}>
          <div className="bg-brand-gray-900 border border-brand-gray-700 rounded-xl p-6 max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="w-6 h-6 text-amber-400" />
              <h3 className="text-lg font-bold text-white">Replace Your Image?</h3>
            </div>
            <p className="text-brand-gray-400 text-sm mb-6">
              This is the image you uploaded. Regenerating will replace it with an AI-rendered cover. Your original will be preserved in the Asset Library.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmRegenerate(false)}
                className="flex-1 px-4 py-2 bg-brand-gray-700 text-white rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={confirmRegenerate}
                className="flex-1 px-4 py-2 bg-brand-orange text-black rounded-lg text-sm font-bold"
              >
                Replace Image
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Compose Form Component
const ComposeForm = ({ onSubmit, isLoading }) => {
  const [mode, setMode] = useState('brief'); // 'brief' | 'image'
  const [brief, setBrief] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState(['twitter', 'linkedin']);
  const [files, setFiles] = useState([]);
  const [isCarousel, setIsCarousel] = useState(false);
  const [autoFit, setAutoFit] = useState(false);
  const [count, setCount] = useState(1);

  const platforms = ['twitter', 'linkedin', 'facebook', 'instagram', 'bluesky', 'threads', 'tiktok', 'youtube', 'reddit'];

  const togglePlatform = (platform) => {
    setSelectedPlatforms(prev => 
      prev.includes(platform) 
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files);
    const validFiles = newFiles.filter(f => 
      ['image/png', 'image/jpeg', 'image/webp'].includes(f.type) && 
      f.size <= 12 * 1024 * 1024
    );
    setFiles(prev => [...prev, ...validFiles].slice(0, 10));
  };

  const removeFile = (idx) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = () => {
    if (selectedPlatforms.length === 0) {
      toast.error('Please select at least one platform');
      return;
    }

    if (mode === 'brief' && !brief.trim()) {
      toast.error('Please enter a brief');
      return;
    }

    if (mode === 'image' && files.length === 0) {
      toast.error('Please upload at least one image');
      return;
    }

    onSubmit({
      mode,
      brief: brief.trim(),
      platforms: selectedPlatforms,
      files,
      isCarousel: isCarousel && files.length > 1,
      autoFit,
      count: mode === 'brief' ? count : 1
    });
  };

  return (
    <div className="bg-brand-gray-900 border border-brand-gray-700 rounded-xl p-6 space-y-6" data-testid="compose-form">
      {/* Mode Toggle */}
      <div className="flex gap-2 bg-brand-gray-800 p-1 rounded-lg w-fit">
        <button
          onClick={() => setMode('brief')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            mode === 'brief' 
              ? 'bg-brand-orange text-black' 
              : 'text-brand-gray-400 hover:text-white'
          }`}
        >
          <Sparkles className="w-4 h-4 inline mr-2" />
          Brief → AI Image
        </button>
        <button
          onClick={() => setMode('image')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            mode === 'image' 
              ? 'bg-brand-orange text-black' 
              : 'text-brand-gray-400 hover:text-white'
          }`}
        >
          <Upload className="w-4 h-4 inline mr-2" />
          Your Image → Caption
        </button>
      </div>

      {/* Brief Input */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-brand-gray-300">
          {mode === 'brief' ? 'What do you want to post about?' : 'Caption direction (optional)'}
        </label>
        <textarea
          value={brief}
          onChange={(e) => setBrief(e.target.value)}
          placeholder={mode === 'brief' 
            ? "e.g., Announce our Q3 webinar, highlight cold-brew launch, share team milestone..."
            : "e.g., Focus on the handcrafted quality, mention the limited edition..."
          }
          className="w-full bg-black border border-brand-gray-600 rounded-lg p-4 text-white resize-none focus:border-brand-orange outline-none"
          rows={3}
        />
      </div>

      {/* Image Upload (only in image mode) */}
      {mode === 'image' && (
        <div className="space-y-3">
          <label className="text-sm font-medium text-brand-gray-300">Upload Images</label>
          <div
            className="border-2 border-dashed border-brand-gray-600 rounded-lg p-6 text-center hover:border-brand-orange/50 transition-colors cursor-pointer"
            onClick={() => document.getElementById('image-upload').click()}
          >
            <Upload className="w-8 h-8 text-brand-gray-500 mx-auto mb-2" />
            <p className="text-sm text-brand-gray-400">
              Drag & drop or click to upload
            </p>
            <p className="text-xs text-brand-gray-500 mt-1">
              PNG, JPEG, WEBP • Max 12MB each • Up to 10 files
            </p>
            <input
              id="image-upload"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* File previews */}
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {files.map((file, idx) => (
                <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-brand-gray-700">
                  <img
                    src={URL.createObjectURL(file)}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() => removeFile(idx)}
                    className="absolute top-1 right-1 p-1 bg-black/60 rounded-full hover:bg-red-500 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Carousel & Auto-fit options */}
          {files.length > 1 && (
            <label className="flex items-center gap-2 text-sm text-brand-gray-400">
              <input
                type="checkbox"
                checked={isCarousel}
                onChange={(e) => setIsCarousel(e.target.checked)}
                className="accent-brand-orange"
              />
              Create carousel posts (all images in one post per platform)
            </label>
          )}
          <label className="flex items-center gap-2 text-sm text-brand-gray-400">
            <input
              type="checkbox"
              checked={autoFit}
              onChange={(e) => setAutoFit(e.target.checked)}
              className="accent-brand-orange"
            />
            Auto-fit to platform dimensions
            <span className="text-xs text-brand-gray-500">(deterministic crop only, no AI)</span>
          </label>
        </div>
      )}

      {/* Post count (only in brief mode) */}
      {mode === 'brief' && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-brand-gray-300">Posts per platform</label>
          <div className="flex gap-2">
            {[1, 2, 3].map(n => (
              <button
                key={n}
                onClick={() => setCount(n)}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  count === n 
                    ? 'bg-brand-orange text-black' 
                    : 'bg-brand-gray-700 text-brand-gray-300 hover:bg-brand-gray-600'
                }`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Platform Selection */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-brand-gray-300">Target Platforms</label>
        <div className="flex flex-wrap gap-2">
          {platforms.map(platform => (
            <button
              key={platform}
              onClick={() => togglePlatform(platform)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                selectedPlatforms.includes(platform)
                  ? getPlatformColor(platform)
                  : 'bg-brand-gray-800 text-brand-gray-500 border-brand-gray-700 hover:border-brand-gray-500'
              }`}
            >
              <PlatformIcon platform={platform} className="w-3 h-3" />
              {platform}
            </button>
          ))}
        </div>
      </div>

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={isLoading || selectedPlatforms.length === 0}
        className="w-full py-3 bg-brand-orange hover:bg-brand-orange/90 text-black font-headline uppercase tracking-wider rounded-full flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {isLoading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <Sparkles className="w-5 h-5" />
        )}
        {mode === 'brief' ? 'Generate Drafts' : 'Create Captions'}
      </button>
    </div>
  );
};

// Main Social Queue Component
const SocialQueue = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('draft');
  const [error, setError] = useState(null);
  const [showConnectedAccounts, setShowConnectedAccounts] = useState(false);
  const [connectedAccountsCount, setConnectedAccountsCount] = useState(0);
  const [activeProfile, setActiveProfile] = useState(null);

  // Load user's active profile on mount
  useEffect(() => {
    const loadActiveProfile = async () => {
      if (!user) return;
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${BACKEND_URL}/api/profiles/user/${user.uid}`);
        if (response.ok) {
          const profiles = await response.json();
          // Use the first profile as active, or null if none
          if (profiles && profiles.length > 0) {
            const profile = profiles[0];
            setActiveProfile({
              name: profile.business_name || profile.name,
              type: profile.profile_type || profile.type || 'business',
              description: profile.business_description || profile.description,
              website: profile.website,
              instructions: profile.custom_instructions || profile.instructions
            });
          }
        }
      } catch (err) {
        console.error('Failed to load profile:', err);
      }
    };
    loadActiveProfile();
  }, [user]);

  // Load connected accounts count on mount
  useEffect(() => {
    const loadConnectedAccounts = async () => {
      if (!user) return;
      try {
        const data = await socialQueueApi.getConnectedAccounts(user.uid);
        const connected = (data.accounts || []).filter(a => a.connected);
        setConnectedAccountsCount(connected.length);
      } catch (err) {
        console.error('Failed to load connected accounts:', err);
      }
    };
    loadConnectedAccounts();
  }, [user]);

  const loadPosts = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    setError(null);
    try {
      const data = await socialQueueApi.getPosts(user.uid, activeTab === 'all' ? null : activeTab);
      setPosts(data.posts || []);
    } catch (err) {
      console.error('Failed to load posts:', err);
      setError('Failed to load posts');
    } finally {
      setIsLoading(false);
    }
  }, [user, activeTab]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const handleCompose = async (data) => {
    if (!user) return;
    setIsCreating(true);
    try {
      let result;
      if (data.mode === 'brief') {
        result = await socialQueueApi.createDraft(
          user.uid, 
          data.brief, 
          data.platforms, 
          data.count,
          null, // styleOverride
          activeProfile // Pass profile context for personalized generation
        );
      } else {
        result = await socialQueueApi.createFromImages(
          user.uid,
          data.files,
          data.platforms,
          data.isCarousel ? 'carousel' : 'single',
          data.brief,
          data.autoFit
        );
      }
      
      if (result.posts && result.posts.length > 0) {
        setPosts(prev => [...result.posts, ...prev]);
        toast.success(`Created ${result.posts.length} draft${result.posts.length > 1 ? 's' : ''}`);
      }
    } catch (err) {
      console.error('Failed to create posts:', err);
      toast.error('Failed to create posts');
    } finally {
      setIsCreating(false);
    }
  };

  const handleUpdate = async (postId, updates) => {
    try {
      const updated = await socialQueueApi.updatePost(postId, updates);
      setPosts(prev => prev.map(p => p.id === postId ? updated : p));
      toast.success('Post updated');
    } catch (err) {
      toast.error('Failed to update post');
    }
  };

  const handleDelete = async (postId) => {
    try {
      await socialQueueApi.deletePost(postId);
      setPosts(prev => prev.filter(p => p.id !== postId));
      toast.success('Post deleted');
    } catch (err) {
      toast.error('Failed to delete post');
    }
  };

  const handlePublish = async (postId, scheduleFor) => {
    try {
      const result = await socialQueueApi.publishPost(postId, user.uid, scheduleFor);
      await loadPosts();
      
      if (result.status === 'published') {
        toast.success('Post published!');
      } else if (result.status === 'queued') {
        toast.success('Post scheduled!');
      } else {
        toast.warning('Some platforms failed to publish');
      }
    } catch (err) {
      toast.error('Failed to publish post');
    }
  };

  const handleGenerateImage = async (postId) => {
    try {
      await socialQueueApi.generateImage(postId);
      setPosts(prev => prev.map(p => 
        p.id === postId ? { ...p, image_status: 'generating' } : p
      ));
      toast.info('Generating image...');
      
      // Poll for completion
      const pollInterval = setInterval(async () => {
        const data = await socialQueueApi.getPosts(user.uid, activeTab === 'all' ? null : activeTab);
        const updatedPost = data.posts?.find(p => p.id === postId);
        if (updatedPost && updatedPost.image_status !== 'generating') {
          setPosts(data.posts);
          clearInterval(pollInterval);
          if (updatedPost.image_status === 'ready') {
            toast.success('Image generated!');
          } else {
            toast.error('Image generation failed');
          }
        }
      }, 3000);
      
      // Clear interval after 60 seconds max
      setTimeout(() => clearInterval(pollInterval), 60000);
    } catch (err) {
      toast.error('Failed to generate image');
    }
  };

  const tabs = [
    { id: 'draft', label: 'Drafts', count: posts.filter(p => p.status === 'draft').length },
    { id: 'queued', label: 'Queued', count: posts.filter(p => p.status === 'queued').length },
    { id: 'published', label: 'Published', count: posts.filter(p => p.status === 'published').length },
  ];

  const filteredPosts = activeTab === 'all' 
    ? posts 
    : posts.filter(p => p.status === activeTab);

  return (
    <div className="space-y-6" data-testid="social-queue">
      {/* Connect Platforms Banner */}
      <div className="bg-brand-gray-900 border border-brand-gray-700 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-brand-orange/10 rounded-lg">
            <Link2 className="w-5 h-5 text-brand-orange" />
          </div>
          <div>
            <h3 className="font-medium text-white">Connected Platforms</h3>
            <p className="text-sm text-brand-gray-400">
              {connectedAccountsCount > 0 
                ? `${connectedAccountsCount} platform${connectedAccountsCount > 1 ? 's' : ''} connected`
                : 'Connect your social accounts to publish directly'
              }
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowConnectedAccounts(true)}
          data-testid="connect-platforms-btn"
          className="flex items-center gap-2 px-4 py-2 bg-brand-orange hover:bg-brand-orange/90 text-black text-sm font-bold rounded-lg transition-colors"
        >
          <Settings className="w-4 h-4" />
          {connectedAccountsCount > 0 ? 'Manage Connections' : 'Connect Platforms'}
        </button>
      </div>

      {/* Active Profile Banner */}
      {activeProfile && (
        <div className="bg-brand-gray-900/50 border border-brand-gray-800 rounded-xl p-3 flex items-center gap-3">
          <div className="p-1.5 bg-brand-gray-800 rounded-lg">
            {activeProfile.type === 'personal' ? (
              <UserIcon className="w-4 h-4 text-brand-gray-400" />
            ) : (
              <Sparkles className="w-4 h-4 text-brand-orange" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white font-medium truncate">
              Using profile: <span className="text-brand-orange">{activeProfile.name}</span>
            </p>
            {activeProfile.description && (
              <p className="text-xs text-brand-gray-500 truncate">{activeProfile.description}</p>
            )}
          </div>
        </div>
      )}

      {/* Compose Form */}
      <ComposeForm onSubmit={handleCompose} isLoading={isCreating} />

      {/* Tabs */}
      <div className="flex gap-4 border-b border-brand-gray-700">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`pb-3 px-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-brand-orange text-brand-orange'
                : 'border-transparent text-brand-gray-400 hover:text-white'
            }`}
          >
            {tab.label}
            {tab.count > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-brand-gray-700 rounded-full text-xs">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-900/20 border border-red-800/50 rounded-lg text-red-400 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-brand-orange animate-spin" />
        </div>
      )}

      {/* Posts Grid */}
      {!isLoading && filteredPosts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPosts.map(post => (
            <SocialPostCard
              key={post.id}
              post={post}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              onPublish={handlePublish}
              onGenerateImage={handleGenerateImage}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && filteredPosts.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-brand-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <Send className="w-8 h-8 text-brand-gray-500" />
          </div>
          <h3 className="text-lg font-medium text-white mb-2">No {activeTab} posts</h3>
          <p className="text-brand-gray-400 text-sm">
            Create your first post using the form above!
          </p>
        </div>
      )}

      {/* Connected Accounts Modal */}
      {showConnectedAccounts && (
        <ConnectedAccounts 
          onClose={async () => {
            setShowConnectedAccounts(false);
            // Refresh connected accounts count
            if (user) {
              try {
                const data = await socialQueueApi.getConnectedAccounts(user.uid);
                const connected = (data.accounts || []).filter(a => a.connected);
                setConnectedAccountsCount(connected.length);
              } catch (err) {
                console.error('Failed to refresh connected accounts:', err);
              }
            }
          }}
        />
      )}
    </div>
  );
};

export default SocialQueue;
